import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getApiErrorMessage } from '../../services/api';
import DetailListIcon from '../../assets/detail-list-icon.svg'

export default function AdminDashboard() {
    const navigate = useNavigate();

    const [events, setEvents] = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingDiscounts, setLoadingDiscounts] = useState(true);

    const [editingEvent, setEditingEvent] = useState(null);
    const [newEvent, setNewEvent] = useState({
        title: '',
        date: '',
        location: '',
        description: '',
        price: 0,
        active: true,
        free_tickets_threshold: '',
        max_free_tickets_per_purchase: ''
    });

    const [editingDiscount, setEditingDiscount] = useState(null);
    const [newDiscount, setNewDiscount] = useState({
        event_id: '',
        code: '',
        type: 'percentage',
        value: 0,
        buy_quantity: '',
        pay_quantity: '',
        active: true
    });

    const fetchEvents = useCallback(async () => {
        try {
            const res = await api.get('/admin/events');
            setEvents(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                navigate('/admin/login');
            }
        } finally {
            setLoadingEvents(false);
        }
    }, [navigate]);

    const fetchDiscounts = useCallback(async () => {
        try {
            const res = await api.get('/admin/discounts');
            setDiscounts(res.data);
        } catch (err) {
            console.error('Error fetching discounts', err);
        } finally {
            setLoadingDiscounts(false);
        }
    }, []);


    useEffect(() => {
        fetchEvents();
        fetchDiscounts();
    }, [fetchEvents, fetchDiscounts]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) navigate('/admin/login');
    }, [navigate])

    const handleEventFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const eventData = editingEvent || newEvent;
            const payload = { ...eventData };
            if (payload.free_tickets_threshold === '' || payload.free_tickets_threshold === null) {
                delete payload.free_tickets_threshold;
            } else {
                payload.free_tickets_threshold = parseInt(payload.free_tickets_threshold, 10);
            }
            if (payload.max_free_tickets_per_purchase === '' || payload.max_free_tickets_per_purchase === null) {
                delete payload.max_free_tickets_per_purchase;
            } else {
                payload.max_free_tickets_per_purchase = parseInt(payload.max_free_tickets_per_purchase, 10);
            }
            if (eventData.price !== undefined && eventData.price !== '') {
                payload.price = parseFloat(eventData.price);
            }

            if (editingEvent) {
                await api.put(`/admin/events/${editingEvent.id}`, payload);
                setEditingEvent(null);
            } else {
                await api.post('/admin/events', payload);
                setNewEvent({
                    title: '', date: '', location: '', description: '', price: 0, active: true, free_tickets_threshold: '', max_free_tickets_per_purchase: ''
                });
            }
            fetchEvents();
        } catch (err) {
            alert(`Failed to ${editingEvent ? 'update' : 'create'} event. ` + getApiErrorMessage(err));
        }
    };

    const handleDeleteEvent = async (id) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        try {
            await api.delete(`/admin/events/${id}`);
            fetchEvents();
        } catch (err) {
            alert('Failed to delete event.');
        }
    };

    const handleSeeMoreEvent = async (id) => {
        navigate(`/admin/tickets/${id}`);
    };

    const startEditEvent = (event) => {
        const date = new Date(event.date);
        const formattedDate = date.toISOString().slice(0, 16);
        setEditingEvent({ ...event, date: formattedDate, price: event.price || 0, active: event.active ?? true, free_tickets_threshold: event.free_tickets_threshold ?? '', max_free_tickets_per_purchase: event.max_free_tickets_per_purchase ?? '' });
    };

    const handleDiscountFormSubmit = async (e) => {
        e.preventDefault();
        const discountData = editingDiscount || newDiscount;
        let payload;
        if (discountData.type === 'x_for_y') {
            const buy = parseInt(discountData.buy_quantity, 10);
            const pay = parseInt(discountData.pay_quantity, 10);
            if (isNaN(buy) || buy < 2 || buy > 10) {
                alert('"Llevas (N)" debe ser un numero entero entre 2 y 10.');
                return;
            }
            if (isNaN(pay) || pay >= buy) {
                alert('"Pagas (Y)" debe ser un numero entero menor que "Llevas (N)".');
                return;
            }
            payload = {
                event_id: discountData.event_id,
                code: discountData.code,
                type: 'x_for_y',
                value: 0,
                buy_quantity: buy,
                pay_quantity: pay,
                active: discountData.active
            };
        } else {
            payload = {
                event_id: discountData.event_id,
                code: discountData.code,
                type: discountData.type,
                value: discountData.value,
                active: discountData.active
            };
        }
        try {
            if (editingDiscount) {
                await api.put(`/admin/discounts/${editingDiscount.id}`, payload);
                setEditingDiscount(null);
            } else {
                await api.post('/admin/discounts', payload);
                setNewDiscount({
                    event_id: '', code: '', type: 'percentage', value: 0, buy_quantity: '', pay_quantity: '', active: true
                });
            }
            fetchDiscounts();
        } catch (err) {
            alert(`Failed to ${editingDiscount ? 'update' : 'create'} discount. ` + getApiErrorMessage(err));
        }
    };

    const handleDeleteDiscount = async (id) => {
        if (!window.confirm('Are you sure you want to delete this discount?')) return;
        try {
            await api.delete(`/admin/discounts/${id}`);
            fetchDiscounts();
        } catch (err) {
            alert('Failed to delete discount.');
        }
    };

    const startEditDiscount = (discount) => {
        setEditingDiscount({
            ...discount,
            buy_quantity: discount.buy_quantity ?? '',
            pay_quantity: discount.pay_quantity ?? ''
        });
    };

    const handleLogout = async () => {
        try {
            await api.post('/logout');
            localStorage.removeItem('token');
            navigate('/admin/login')
        } catch (error) {
            console.error('Ocurrio un error', error)
        }
    }

    return (
        <div className="max-w-7xl mx-auto space-y-12 mb-12">
            <div>
                <div className='flex items-baseline justify-between'>
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2 grow-11">Gestion de Eventos</h2>
                    <button onClick={handleLogout} className='bg-indigo-600 ps-2 pe-2 p-1 rounded'><h2 className='text-white'>Salir</h2></button>
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-1 bg-white rounded-lg shadow p-6 h-fit">
                        <h3 className="text-xl font-semibold mb-4 border-b pb-2 text-indigo-700">
                            {editingEvent ? 'Editar evento' : 'Crear evento'}
                        </h3>
                        <form onSubmit={handleEventFormSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Nombre de evento</label>
                                <input required type="text" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-indigo-500" value={editingEvent ? editingEvent.title : newEvent.title} onChange={e => editingEvent ? setEditingEvent({ ...editingEvent, title: e.target.value }) : setNewEvent({ ...newEvent, title: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Dia y hora</label>
                                <input required type="datetime-local" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-indigo-500" value={editingEvent ? editingEvent.date : newEvent.date} onChange={e => editingEvent ? setEditingEvent({ ...editingEvent, date: e.target.value }) : setNewEvent({ ...newEvent, date: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Lugar</label>
                                <input type="text" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-indigo-500" value={editingEvent ? editingEvent.location : newEvent.location} onChange={e => editingEvent ? setEditingEvent({ ...editingEvent, location: e.target.value }) : setNewEvent({ ...newEvent, location: e.target.value })} />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700">Precio</label>
                                    <input required type="number" step="0.01" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-indigo-500" value={editingEvent ? editingEvent.price : newEvent.price} onChange={e => editingEvent ? setEditingEvent({ ...editingEvent, price: e.target.value }) : setNewEvent({ ...newEvent, price: e.target.value })} />
                                </div>
                                <div className="flex items-center mt-6">
                                    <input type="checkbox" id="activeEvent" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={editingEvent ? editingEvent.active : newEvent.active} onChange={e => editingEvent ? setEditingEvent({ ...editingEvent, active: e.target.checked }) : setNewEvent({ ...newEvent, active: e.target.checked })} />
                                    <label htmlFor="activeEvent" className="ml-2 block text-sm text-gray-900">Activo</label>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Entradas pagas umbral</label>
                                    <input type="number" min="1" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-indigo-500" placeholder="Opcional" value={editingEvent ? editingEvent.free_tickets_threshold : newEvent.free_tickets_threshold} onChange={e => editingEvent ? setEditingEvent({ ...editingEvent, free_tickets_threshold: e.target.value }) : setNewEvent({ ...newEvent, free_tickets_threshold: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Max gratis por compra</label>
                                    <input type="number" min="1" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-indigo-500" placeholder="Opcional" value={editingEvent ? editingEvent.max_free_tickets_per_purchase : newEvent.max_free_tickets_per_purchase} onChange={e => editingEvent ? setEditingEvent({ ...editingEvent, max_free_tickets_per_purchase: e.target.value }) : setNewEvent({ ...newEvent, max_free_tickets_per_purchase: e.target.value })} />
                                </div>
                            </div>
                            <p className="text-xs text-gray-500">Completar ambos o dejar ambos vacios para quitar promo</p>

                            <div className="flex gap-2">
                                {editingEvent && (
                                    <button type="button" onClick={() => setEditingEvent(null)} className="flex-1 mt-6 bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded hover:bg-gray-300 transition">Cancelar</button>
                                )}
                                <button type="submit" className="flex-grow mt-6 bg-indigo-600 text-white font-bold py-2 px-4 rounded hover:bg-indigo-700 transition">
                                    {editingEvent ? 'Actualizar evento' : 'Crear evento'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="md:col-span-2">
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="p-6 border-b"><h3 className="text-xl font-semibold text-gray-800">Eventos</h3></div>
                            {loadingEvents ? (
                                <div className="p-6 text-center text-gray-500">Cargando eventos...</div>
                            ) : events.length === 0 ? (
                                <div className="p-6 text-center text-gray-500">No hay eventos creados</div>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {events.map(event => (
                                        <li key={event.id} className={`p-6 hover:bg-gray-50 transition ${editingEvent?.id === event.id ? 'bg-indigo-50' : ''}`}>
                                            <div className="flex justify-between items-start">
                                                <div className="flex-grow">
                                                    <h4 className="text-lg font-bold text-gray-900">{event.title} <span className={`ml-2 text-xs px-2 py-1 rounded-full ${event.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{event.active ? 'Activo' : 'Inactivo'}</span></h4>
                                                    <p className="text-sm text-gray-500 mb-1">Fecha: {new Date(event.date).toLocaleString()} | Lugar: {event.location || 'TBA'}</p>
                                                    <p className="text-sm font-semibold text-gray-700">Precio: ${event.price}</p>
                                                    {(event.free_tickets_threshold && event.max_free_tickets_per_purchase) ? (
                                                        <p className="text-xs text-green-700 mt-1">Promo: {event.free_tickets_threshold} pagas + {event.max_free_tickets_per_purchase} gratis (max {event.free_tickets_threshold + event.max_free_tickets_per_purchase})</p>
                                                    ) : (
                                                        <p className="text-xs text-gray-500 mt-1">Sin promo de gratis (max 10)</p>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    <button onClick={() => startEditEvent(event)} className="p-2 text-indigo-600 hover:bg-indigo-100 rounded transition" title="Editar evento">✎</button>
                                                    <button onClick={() => handleDeleteEvent(event.id)} className="p-2 text-red-600 hover:bg-red-100 rounded transition" title="Borrar evento">🗑</button>
                                                    <button onClick={() => handleSeeMoreEvent(event.id)} className="text-indigo-600 p-2 hover:bg-gray-100 rounded transition" title='Ver entradas'><img src={DetailListIcon} width={16} height={16} alt='Detalle' /></button>
                                                    <button onClick={() => navigate(`/admin/ranking/${event.id}`)} className="p-2 text-amber-600 hover:bg-amber-100 rounded transition" title="Ver ranking">🏆</button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2">Gestion de Cupones</h2>
                <div className="grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-1 bg-white rounded-lg shadow p-6 h-fit">
                        <h3 className="text-xl font-semibold mb-4 border-b pb-2 text-teal-600">
                            {editingDiscount ? 'Editar Cupon' : 'Crear Cupon'}
                        </h3>
                        <form onSubmit={handleDiscountFormSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Evento</label>
                                <select required className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-teal-500" value={editingDiscount ? editingDiscount.event_id : newDiscount.event_id} onChange={e => editingDiscount ? setEditingDiscount({ ...editingDiscount, event_id: e.target.value }) : setNewDiscount({ ...newDiscount, event_id: e.target.value })}>
                                    <option value="">Seleccione un evento</option>
                                    {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Codigo</label>
                                <input required type="text" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-teal-500 uppercase" placeholder="PROMO2026" value={editingDiscount ? editingDiscount.code : newDiscount.code} onChange={e => editingDiscount ? setEditingDiscount({ ...editingDiscount, code: e.target.value.toUpperCase() }) : setNewDiscount({ ...newDiscount, code: e.target.value.toUpperCase() })} />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700">Tipo</label>
                                    <select required className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-teal-500" value={editingDiscount ? editingDiscount.type : newDiscount.type} onChange={e => {
                                        const type = e.target.value;
                                        if (editingDiscount) {
                                            setEditingDiscount({
                                                ...editingDiscount,
                                                type,
                                                value: type === 'x_for_y' ? 0 : editingDiscount.value,
                                                buy_quantity: type === 'x_for_y' ? editingDiscount.buy_quantity : '',
                                                pay_quantity: type === 'x_for_y' ? editingDiscount.pay_quantity : ''
                                            });
                                        } else {
                                            setNewDiscount({
                                                ...newDiscount,
                                                type,
                                                value: type === 'x_for_y' ? 0 : newDiscount.value,
                                                buy_quantity: type === 'x_for_y' ? newDiscount.buy_quantity : '',
                                                pay_quantity: type === 'x_for_y' ? newDiscount.pay_quantity : ''
                                            });
                                        }
                                    }}>
                                        <option value="percentage">Porcentaje (%)</option>
                                        <option value="fixed">Fijo ($)</option>
                                        <option value="x_for_y">Promo NxY (Llevas N, pagas Y)</option>
                                    </select>
                                </div>
                                {(editingDiscount ? editingDiscount.type : newDiscount.type) !== 'x_for_y' && (
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700">Valor</label>
                                        <input required type="number" step="0.01" min="0" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-teal-500" value={editingDiscount ? editingDiscount.value : newDiscount.value} onChange={e => editingDiscount ? setEditingDiscount({ ...editingDiscount, value: e.target.value }) : setNewDiscount({ ...newDiscount, value: e.target.value })} />
                                    </div>
                                )}
                            </div>
                            {(editingDiscount ? editingDiscount.type : newDiscount.type) === 'x_for_y' && (
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700">Llevas (N)</label>
                                        <input required type="number" min="2" max="10" step="1" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-teal-500" placeholder="6" value={editingDiscount ? editingDiscount.buy_quantity : newDiscount.buy_quantity} onChange={e => editingDiscount ? setEditingDiscount({ ...editingDiscount, buy_quantity: e.target.value }) : setNewDiscount({ ...newDiscount, buy_quantity: e.target.value })} />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700">Pagas (Y)</label>
                                        <input required type="number" min="1" step="1" className="mt-1 w-full p-2 border rounded focus:outline-none focus:border-teal-500" placeholder="4" value={editingDiscount ? editingDiscount.pay_quantity : newDiscount.pay_quantity} onChange={e => editingDiscount ? setEditingDiscount({ ...editingDiscount, pay_quantity: e.target.value }) : setNewDiscount({ ...newDiscount, pay_quantity: e.target.value })} />
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center mt-4">
                                <input type="checkbox" id="activeDiscount" className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded" checked={editingDiscount ? editingDiscount.active : newDiscount.active} onChange={e => editingDiscount ? setEditingDiscount({ ...editingDiscount, active: e.target.checked }) : setNewDiscount({ ...newDiscount, active: e.target.checked })} />
                                <label htmlFor="activeDiscount" className="ml-2 block text-sm text-gray-900">Cupon Activo</label>
                            </div>

                            <div className="flex gap-2">
                                {editingDiscount && (
                                    <button type="button" onClick={() => setEditingDiscount(null)} className="flex-1 mt-6 bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded hover:bg-gray-300 transition">Cancelar</button>
                                )}
                                <button type="submit" className="flex-grow mt-6 bg-teal-600 text-white font-bold py-2 px-4 rounded hover:bg-teal-700 transition">
                                    {editingDiscount ? 'Actualizar Cupon' : 'Crear Cupon'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="md:col-span-2">
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <div className="p-6 border-b"><h3 className="text-xl font-semibold text-gray-800">Cupones Existentes</h3></div>
                            {loadingDiscounts ? (
                                <div className="p-6 text-center text-gray-500">Cargando cupones...</div>
                            ) : discounts.length === 0 ? (
                                <div className="p-6 text-center text-gray-500">No hay cupones creados</div>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {discounts.map(discount => {
                                        const eventName = events.find(e => e.id === discount.event_id)?.title || 'Evento Desconocido';
                                        return (
                                            <li key={discount.id} className={`p-6 hover:bg-gray-50 transition ${editingDiscount?.id === discount.id ? 'bg-teal-50' : ''}`}>
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-grow">
                                                        <h4 className="text-lg font-bold text-gray-900">{discount.code} <span className={`ml-2 text-xs px-2 py-1 rounded-full ${discount.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{discount.active ? 'Activo' : 'Inactivo'}</span></h4>
                                                        <p className="text-sm text-gray-500 mb-2">Evento: {eventName}</p>
                                                        <p className="text-sm font-semibold text-gray-700">{discount.type === 'x_for_y'
                                                            ? `Promo: ${discount.buy_quantity}X${discount.pay_quantity} (Llevas ${discount.buy_quantity}, pagas ${discount.pay_quantity})`
                                                            : `Descuento: ${discount.type === 'percentage' ? `${discount.value}%` : `$${discount.value}`}`}</p>
                                                    </div>
                                                    <div className="flex gap-2 ml-4">
                                                        <button onClick={() => startEditDiscount(discount)} className="p-2 text-teal-600 hover:bg-teal-100 rounded transition" title="Editar cupon">✎</button>
                                                        <button onClick={() => handleDeleteDiscount(discount.id)} className="p-2 text-red-600 hover:bg-red-100 rounded transition" title="Borrar cupon">🗑</button>
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
